import {
  HandlerTimeoutError,
  SessionUnavailableError,
  formatErrorForLogging,
} from '@eluve/eluve-errors';

import { selectNextActiveTask } from './selectNextActiveTask';
import {
  AppointmentTask,
  RetryConfig,
  TaskProcessorDependencies,
  TaskProcessorInput,
  TaskProcessorInterface,
} from './types';

const DEFAULT_FOREGROUND_RETRY_CONFIG: Required<RetryConfig> = {
  maxRetries: 5,
  initialDelayMs: 1000, // 1 second
  maxDelayMs: 30000, // 30 seconds
  backoffMultiplier: 2,
};

const DEFAULT_BACKGROUND_RETRY_CONFIG: Required<RetryConfig> = {
  maxRetries: 10,
  initialDelayMs: 250, // 250 milliseconds
  maxDelayMs: 5000, // 5 seconds
  backoffMultiplier: 2,
};

const HANDLER_TIMEOUT_MS = 60_000;

/**
 * Processes appointment tasks with one serial lane per appointment. Lanes
 * for different appointments run concurrently, so a hung or retrying task
 * for one appointment cannot block tasks for any other appointment.
 *
 * Each handler invocation gets a fresh AbortController; when the handler
 * exceeds HANDLER_TIMEOUT_MS, the controller is aborted so the in-flight
 * network request is cancelled and the task flows through the existing
 * retry/backoff path.
 */
export class TaskProcessor implements TaskProcessorInterface {
  private userId: string;
  private tenantId: string;
  private _tasks: AppointmentTask[] = [];
  private processingAppointments = new Set<string>();
  public isInitialized = false;
  private isPaused = false;
  private dependencies: TaskProcessorDependencies;
  private foregroundRetryConfig: Required<RetryConfig>;
  private backgroundRetryConfig: Required<RetryConfig>;
  private retryTimeoutsByAppointment = new Map<string, NodeJS.Timeout>();

  constructor({ userId, tenantId, dependencies }: TaskProcessorInput) {
    this.userId = userId;
    this.tenantId = tenantId;
    this.dependencies = dependencies;

    this.foregroundRetryConfig = {
      ...DEFAULT_FOREGROUND_RETRY_CONFIG,
      ...dependencies.retryConfig?.foreground,
    };

    this.backgroundRetryConfig = {
      ...DEFAULT_BACKGROUND_RETRY_CONFIG,
      ...dependencies.retryConfig?.background,
    };
  }

  async initialize() {
    if (this.isInitialized) {
      return;
    }

    try {
      this._tasks = this.dependencies.getPersistedTasks(
        this.tenantId,
        this.userId,
      );
      this.isInitialized = true;

      if (this._tasks.length > 0) {
        this.dependencies.logger.info(
          'TaskProcessor initialized with persisted tasks',
          {
            taskCount: this._tasks.length,
            tasks: this._tasks.map((t) => ({
              type: t.type,
              appointmentId: t.appointmentId,
              retryCount: t.retry?.retryCount,
              nextRetryAt: t.retry?.nextRetryAt
                ? new Date(t.retry.nextRetryAt).toISOString()
                : undefined,
            })),
          },
        );

        this.triggerQueueProcessing();
      }
    } catch (error) {
      this.dependencies.logger.error('Failed to initialize TaskProcessor', {
        error,
      });
      throw error;
    }
  }

  async addTask(task: AppointmentTask) {
    if (!this.isInitialized) {
      await this.initialize();
    }

    const taskKey = this.getTaskDeduplicationKey(task);
    const existingTask = this._tasks.find(
      (t) => this.getTaskDeduplicationKey(t) === taskKey,
    );

    if (existingTask) {
      // If existing task has a retry scheduled, clear it to process immediately
      if (existingTask.retry?.nextRetryAt) {
        this.dependencies.logger.info(
          'Task already exists, resetting retry time',
          {
            taskType: task.type,
            appointmentId: task.appointmentId,
          },
        );

        existingTask.retry.nextRetryAt = Date.now();
        this.persistTasks();

        this.triggerQueueProcessing();
      } else {
        this.dependencies.logger.info('Task already in queue, skipping add', {
          taskType: task.type,
          appointmentId: task.appointmentId,
          isPaused: this.isPaused,
          isProcessingAppointment: this.processingAppointments.has(
            task.appointmentId,
          ),
          existingRetryCount: existingTask.retry?.retryCount,
          existingLastAttemptAt: existingTask.retry?.lastAttemptAt
            ? new Date(existingTask.retry.lastAttemptAt).toISOString()
            : undefined,
        });
      }
      return;
    }

    // Initialize retry metadata if not present
    if (!task.retry) {
      task.retry = {
        retryCount: 0,
      };
    }

    // If task has existing retry metadata (retryCount > 0 and lastAttemptAt),
    // calculate nextRetryAt based on the backoff schedule
    if (
      task.retry.retryCount > 0 &&
      task.retry.lastAttemptAt &&
      !task.retry.nextRetryAt
    ) {
      task.retry.nextRetryAt = this.calculateNextRetryTime(task);

      this.dependencies.logger.info(
        'Task added with existing retry metadata, calculated nextRetryAt',
        {
          taskType: task.type,
          appointmentId: task.appointmentId,
          retryCount: task.retry.retryCount,
          lastAttemptAt: new Date(task.retry.lastAttemptAt).toISOString(),
          nextRetryAt: new Date(task.retry.nextRetryAt).toISOString(),
        },
      );
    }

    this._tasks.push(task);
    this.persistTasks();

    this.triggerQueueProcessing();
  }

  /**
   * Spawns a lane processor for every appointment that has tasks and isn't
   * already being processed. Each lane runs serially; lanes for different
   * appointments run concurrently.
   */
  private triggerQueueProcessing() {
    if (this.isPaused) {
      return;
    }

    const appointmentIds = new Set<string>();
    for (const task of this._tasks) {
      appointmentIds.add(task.appointmentId);
    }

    for (const appointmentId of appointmentIds) {
      if (!this.processingAppointments.has(appointmentId)) {
        // Defer so the caller (e.g. addTask) can finish synchronously first
        setTimeout(() => this.processAppointmentLane(appointmentId), 0);
      }
    }
  }

  private async processAppointmentLane(appointmentId: string) {
    if (this.processingAppointments.has(appointmentId) || this.isPaused) {
      return;
    }

    this.processingAppointments.add(appointmentId);

    try {
      while (true) {
        if (this.isPaused) {
          break;
        }

        const laneTasks = this._tasks.filter(
          (t) => t.appointmentId === appointmentId,
        );
        const nextTask = selectNextActiveTask(laneTasks);

        if (!nextTask) {
          break;
        }

        const success = await this.processTask(nextTask);

        if (success) {
          // Remove successful tasks
          this._tasks = this._tasks.filter((task) => task !== nextTask);
        }
        this.persistTasks();
      }
    } catch (error) {
      this.dependencies.logger.error('Error processing appointment lane', {
        error,
        appointmentId,
      });
    } finally {
      this.processingAppointments.delete(appointmentId);

      // Schedule next attempt if this lane still has pending tasks
      const remainingLaneTasks = this._tasks.filter(
        (t) => t.appointmentId === appointmentId,
      );
      if (remainingLaneTasks.length > 0 && !this.isPaused) {
        this.scheduleNextProcessing(appointmentId, remainingLaneTasks);
      }
    }
  }

  private isBackgroundTask(task: AppointmentTask): boolean {
    return Boolean(task.isBackgroundTask);
  }

  private getRetryConfigForTask(task: AppointmentTask): Required<RetryConfig> {
    const isBackground = this.isBackgroundTask(task);
    const baseRetryConfig = isBackground
      ? this.backgroundRetryConfig
      : this.foregroundRetryConfig;
    const taskSpecificConfig = isBackground
      ? task.retryConfig?.background
      : task.retryConfig?.foreground;

    return {
      ...baseRetryConfig,
      ...taskSpecificConfig,
    };
  }

  private calculateNextRetryTime(task: AppointmentTask): number {
    const retryConfig = this.getRetryConfigForTask(task);

    const retryCount = task.retry?.retryCount ?? 0;
    const delay = Math.min(
      retryConfig.initialDelayMs *
        Math.pow(retryConfig.backoffMultiplier, Math.max(0, retryCount - 1)),
      retryConfig.maxDelayMs,
    );

    const baseTime = task.retry?.lastAttemptAt || Date.now();
    return baseTime + delay;
  }

  /**
   * Checks if an error's name or message matches any of the given patterns.
   * Uses string matching since libraries like Apollo wrap the original error.
   */
  private errorMatchesPatterns(error: any, patterns: string[]): boolean {
    if (!error) return false;

    const errorName = error?.name?.toLowerCase() ?? '';
    if (patterns.some((p) => errorName.includes(p))) return true;

    const errorMessage = formatErrorForLogging(error).toLowerCase();
    return patterns.some((p) => errorMessage.includes(p));
  }

  private classifyError(error: unknown): 'session' | 'network' | undefined {
    if (this.isSessionError(error)) return 'session';
    if (this.isNetworkError(error)) return 'network';
    return undefined;
  }

  private isSessionError(error: unknown): boolean {
    return SessionUnavailableError.isInstanceOf(error);
  }

  private isNetworkError(error: unknown): boolean {
    return this.errorMatchesPatterns(error, [
      'networkerror',
      'networktimeouterror',
      'failed to fetch',
    ]);
  }

  private scheduleNextProcessing(
    appointmentId: string,
    laneTasks: AppointmentTask[],
  ) {
    const existing = this.retryTimeoutsByAppointment.get(appointmentId);
    if (existing) {
      clearTimeout(existing);
    }

    const now = Date.now();
    const nextRetryTask = laneTasks
      .filter((task) => task.retry?.nextRetryAt)
      .sort(
        (a, b) => (a.retry?.nextRetryAt ?? 0) - (b.retry?.nextRetryAt ?? 0),
      )[0];

    const delay = nextRetryTask?.retry?.nextRetryAt
      ? Math.max(0, nextRetryTask.retry.nextRetryAt - now)
      : 100; // Fallback for tasks without retry schedule

    const timeout = setTimeout(() => {
      this.retryTimeoutsByAppointment.delete(appointmentId);
      this.processAppointmentLane(appointmentId);
    }, delay);

    this.retryTimeoutsByAppointment.set(appointmentId, timeout);
  }

  private async processTask(task: AppointmentTask): Promise<boolean> {
    let timeoutHit = false;
    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
      const handler = this.dependencies.taskHandlers[task.type];

      if (!handler) {
        this.dependencies.logger.warn('Unknown task type', {
          task,
        });
        return true; // Remove unknown task types
      }

      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          timeoutHit = true;
          controller.abort();
          reject(
            new HandlerTimeoutError(
              `Task handler timed out after ${HANDLER_TIMEOUT_MS}ms`,
            ),
          );
        }, HANDLER_TIMEOUT_MS);
      });

      await Promise.race([
        handler(task, {
          userId: this.userId,
          tenantId: this.tenantId,
          signal: controller.signal,
        }),
        timeoutPromise,
      ]);

      return true; // Success
    } catch (error) {
      if (timeoutHit || error instanceof HandlerTimeoutError) {
        this.dependencies.logger.warn('Handler hang detected', {
          taskType: task.type,
          appointmentId: task.appointmentId,
          timeoutMs: HANDLER_TIMEOUT_MS,
          retryCount: task.retry?.retryCount ?? 0,
        });
      }

      const errorType = this.classifyError(error);

      this.dependencies.logger.warn('Task processing failed', {
        error,
        taskType: task.type,
        appointmentId: task.appointmentId,
        retryCount: task.retry?.retryCount ?? 0,
        errorType,
      });

      return this.handleTaskFailure({ task, errorType });
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  private handleTaskFailure({
    task,
    errorType,
  }: {
    task: AppointmentTask;
    errorType?: 'network' | 'session';
  }) {
    if (errorType === 'session') {
      this.dependencies.logger.warn(
        'Session unavailable, pausing task processor until session is restored',
        {
          taskType: task.type,
          appointmentId: task.appointmentId,
        },
      );

      this.pause();
      return false;
    }

    const retryConfig = this.getRetryConfigForTask(task);

    const previousRetryCount = task.retry?.retryCount ?? 0;
    // Network errors don't increment retryCount to avoid dropping tasks on spotty connections
    const retryCount =
      errorType === 'network' ? previousRetryCount : previousRetryCount + 1;

    if (retryCount > retryConfig.maxRetries) {
      // Giving up after exhausting retries is expected operational noise, not
      // an error — the task is dropped either way. Genuine session/logout
      // conditions never reach here (they pause via the `session` branch
      // above), and terminal conditions like a 404 are handled in-handler.
      this.dependencies.logger.warn('Task exceeded max retries, giving up', {
        taskType: task.type,
        appointmentId: task.appointmentId,
        retryCount,
        maxRetries: retryConfig.maxRetries,
      });
      return true; // Remove task that exceeded max retries
    }

    const now = Date.now();

    // Update task retry metadata
    task.retry = {
      retryCount,
      lastAttemptAt: now,
    };

    // Calculate nextRetryAt using the same logic as addTask
    task.retry.nextRetryAt = this.calculateNextRetryTime(task);

    const delay = task.retry.nextRetryAt - now;

    this.dependencies.logger.info('Task scheduled for retry', {
      taskType: task.type,
      appointmentId: task.appointmentId,
      retryCount,
      maxRetries: retryConfig.maxRetries,
      delayMs: delay,
      nextRetryAt: task.retry.nextRetryAt
        ? new Date(task.retry.nextRetryAt).toISOString()
        : undefined,
      errorType,
    });

    return false; // Keep task in queue for retry
  }

  private persistTasks() {
    try {
      this.dependencies.persistAppointmentTasks(
        this.tenantId,
        this.userId,
        this._tasks,
      );
    } catch (error) {
      this.dependencies.logger.error('Failed to persist tasks', { error });
    }
  }

  private getTaskDeduplicationKey(task: AppointmentTask): string {
    const base = `${task.type}:${task.appointmentId}`;

    switch (task.type) {
      case 'TASK.GENERATE_NOTE':
        return `${base}`;
      case 'TASK.UPDATE_SEGMENT_RECORDING_ENDED_AT':
        return `${base}:${task.segmentId}`;
      case 'TASK.MARK_CLIENT_AUDIO_COMPLETED_AT':
        return `${base}:${task.fileId}`;
      case 'TASK.ADD_APPOINTMENT_TAG': {
        const baseWithOptionalFileId = `${base}${task.fileId ? `:${task.fileId}` : ''}`;
        return `${baseWithOptionalFileId}:${task.tag.type}:${task.tag.key}`;
      }
      default:
        return `${base}:${task.fileName}`;
    }
  }

  hasTasksForAppointment(appointmentId: string): boolean {
    return this._tasks.some((task) => task.appointmentId === appointmentId);
  }

  pause() {
    this.dependencies.logger.info('Pausing task processor', {
      taskCount: this._tasks.length,
      tasks: this._tasks.map((t) => ({
        type: t.type,
        appointmentId: t.appointmentId,
      })),
    });

    this.isPaused = true;

    this.clearAllRetryTimeouts();
  }

  resume() {
    if (!this.isInitialized || !this.isPaused) return;

    this.isPaused = false;

    this.resetTasksFromStorage();

    this.dependencies.logger.info('Resuming task processor', {
      taskCount: this._tasks.length,
    });

    // Reset all retry times so tasks process immediately after resume
    const now = Date.now();
    this._tasks.forEach((task) => {
      if (task.retry?.nextRetryAt) {
        task.retry.nextRetryAt = now;
      }
    });

    // Start processing immediately if tasks exist
    if (this._tasks.length > 0) {
      this.triggerQueueProcessing();
    }
  }

  resetTasksFromStorage() {
    this._tasks = this.dependencies.getPersistedTasks(
      this.tenantId,
      this.userId,
    );
  }

  getTaskCount(): number {
    return this._tasks.length;
  }

  getTasks() {
    return [...this._tasks];
  }

  isProcessingTasks(): boolean {
    return this.processingAppointments.size > 0;
  }

  getTenantId(): string {
    return this.tenantId;
  }

  getUserId(): string {
    return this.userId;
  }

  cleanup() {
    this.clearAllRetryTimeouts();
  }

  private clearAllRetryTimeouts() {
    for (const timeout of this.retryTimeoutsByAppointment.values()) {
      clearTimeout(timeout);
    }
    this.retryTimeoutsByAppointment.clear();
  }
}
